/**
 * Wan VACE Service
 *
 * Typed client for the `/api/v1/wan-vace` local endpoints.
 * Wan VACE has its own dedicated router (separate from the generic components
 * router) because it supports multi-model + LoRA management.
 */

import { api } from './api-adapters';

// =============================================================================
// TYPES
// =============================================================================

export type WanVaceModelId = 'vace_1_3b' | 'vace_14b_gguf' | 'vace_14b_full';

export interface WanVaceModelRequirements {
  vramGb: number;
  ramGb: number;
  diskGb: number;
  downloadGb: number;
}

export interface WanVaceStatus {
  installed: boolean;
  version: string | null;
  selectedModel: string;
  installedModels: string[];
  installedLoras: string[];
  installDir: string;
  requirements: Record<string, WanVaceModelRequirements>;
  availableModels: Record<string, string>;
}

export interface WanVaceInstallResponse {
  jobId: string;
  message: string;
}

export interface WanVaceInstallProgress {
  jobId: string;
  status: 'starting' | 'downloading' | 'installing' | 'complete' | 'error' | string;
  progress: number;
  message: string;
  model?: string;
}

export interface WanVaceLoRA {
  id: string;
  name: string;
  installed: boolean;
  downloadGb: number;
  license: string;
  recommendedSteps: number;
  stepReduction: number;
}

// =============================================================================
// RAW BACKEND SHAPES
// =============================================================================

interface RawStatus {
  installed: boolean;
  version: string | null;
  selected_model: string;
  installed_models: string[];
  installed_loras: string[];
  install_dir: string;
  requirements: Record<string, WanVaceModelRequirements>;
  available_models: Record<string, string>;
}

interface RawInstallResponse {
  job_id: string;
  message: string;
}

interface RawProgress {
  job_id: string;
  status: string;
  progress: number;
  message: string;
  model?: string;
}

// =============================================================================
// API METHODS
// =============================================================================

export async function getWanVaceStatus(): Promise<WanVaceStatus> {
  const response = await api.get<RawStatus>('/api/v1/wan-vace/status');
  const data = response.data;
  return {
    installed: data.installed,
    version: data.version,
    selectedModel: data.selected_model,
    installedModels: data.installed_models,
    installedLoras: data.installed_loras,
    installDir: data.install_dir,
    requirements: data.requirements,
    availableModels: data.available_models,
  };
}

export async function installWanVaceModel(
  model: WanVaceModelId | string
): Promise<WanVaceInstallResponse> {
  const response = await api.post<RawInstallResponse>('/api/v1/wan-vace/install', { model });
  return { jobId: response.data.job_id, message: response.data.message };
}

export async function getInstallProgress(jobId: string): Promise<WanVaceInstallProgress> {
  const response = await api.get<RawProgress>(`/api/v1/wan-vace/install/progress/${jobId}`);
  const data = response.data;
  return {
    jobId: data.job_id,
    status: data.status,
    progress: data.progress,
    message: data.message,
    model: data.model,
  };
}

export async function selectWanVaceModel(
  model: WanVaceModelId | string
): Promise<{ model: string; message: string }> {
  const response = await api.post<{ model: string; message: string }>(
    '/api/v1/wan-vace/select-model',
    { model }
  );
  return response.data;
}

export async function uninstallWanVaceModel(modelId: string): Promise<void> {
  await api.delete(`/api/v1/wan-vace/models/${modelId}`);
}

export async function uninstallWanVaceAll(): Promise<void> {
  await api.delete('/api/v1/wan-vace');
}

interface RawLoraListItem {
  id: string;
  name: string;
  installed: boolean;
  downloadGb?: number;
  license?: string;
  recommendedSteps?: number;
  stepReduction?: number;
}

interface RawLoraListResponse {
  loras: RawLoraListItem[];
  installedCount?: number;
}

export async function listWanVaceLoras(): Promise<WanVaceLoRA[]> {
  const response = await api.get<RawLoraListResponse>('/api/v1/wan-vace/loras');
  const raw = response.data.loras ?? [];
  return raw.map((item) => ({
    id: item.id,
    name: item.name,
    installed: item.installed,
    downloadGb: item.downloadGb ?? 0,
    license: item.license ?? 'unknown',
    recommendedSteps: item.recommendedSteps ?? 30,
    stepReduction: item.stepReduction ?? 0,
  }));
}

interface RawLoraInstallResponse {
  job_id: string;
  message: string;
}

export async function installWanVaceLora(loraId: string): Promise<WanVaceInstallResponse> {
  // Wire is snake_case (RULE 1.5) — the backend's camelCase leak was fixed
  // in audit 2026-06-10 Stage 04 (api_server/local_routes/wan_vace.py).
  const response = await api.post<RawLoraInstallResponse>(
    `/api/v1/wan-vace/loras/${loraId}/install`
  );
  return { jobId: response.data.job_id, message: response.data.message };
}

export async function uninstallWanVaceLora(loraId: string): Promise<void> {
  await api.delete(`/api/v1/wan-vace/loras/${loraId}`);
}
